import {
  Box,
  Button,
  Card,
  Flex,
  Radio,
  Spinner,
  Stack,
  Text,
  TextInput
} from "@sanity/ui";
import { useToast } from "@sanity/ui/toast";
import { CopyIcon } from "@sanity/icons/Copy";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import {
  ObjectSchemaType,
  Preview,
  SanityDocument,
  useClient,
  useSchema
} from "sanity";
import useDebounce from "../../utils/useDebounce";
import {
  ClonableType,
  CLONE_CONTENT_CONFIG,
  cloneContent,
  isContentField,
  isSystemField
} from "./cloneContent";

type SourceVersion = "published" | "draft";

const PERSPECTIVES = {
  published: "published",
  draft: "previewDrafts"
} as const;

const DOCUMENTS_BY_CRUISE_CODE_QUERY = `*[
  _type == $type &&
  (upper(cruiseCode) == upper($cruiseCode) || upper(data->cruiseCode) == upper($cruiseCode))
]`;

type SourceQueryResult = SanityDocument & { _originalId?: string };

const publishedId = (id: string) => id.replace(/^drafts\./, "");

type Props = {
  type: ClonableType;
  targetId: string;
  target: SanityDocument;
  onCloned: () => void;
};

const CloneContentDialog: React.FC<Props> = ({
  type,
  targetId,
  target,
  onCloned
}) => {
  const client = useClient({ apiVersion: "2021-10-21" });
  const schemaType = useSchema().get(type) as ObjectSchemaType;
  const typeTitle = (schemaType.title ?? type).toLowerCase();
  const { excludedFields } = CLONE_CONTENT_CONFIG[type];
  const toast = useToast();

  const [cruiseCode, setCruiseCode] = useState("");
  const [sourceVersion, setSourceVersion] =
    useState<SourceVersion>("published");
  const [cloning, setCloning] = useState(false);
  const debouncedCruiseCode = useDebounce(cruiseCode.trim(), 400);

  const fields = useMemo(
    () =>
      schemaType.fields.map(({ name, type }) => ({
        name,
        title: type.title ?? name,
        readOnly: type.readOnly
      })),
    [schemaType]
  );

  const keptFieldTitles = useMemo(
    () =>
      fields
        .filter(
          (field) =>
            !isSystemField(field.name) && !isContentField(excludedFields)(field)
        )
        .map(({ title }) => title)
        .join(", "),
    [fields, excludedFields]
  );

  const sources = useQuery({
    queryKey: ["cloneContentSource", type, debouncedCruiseCode, sourceVersion],
    enabled: debouncedCruiseCode.length > 0,
    queryFn: () =>
      client
        .withConfig({ perspective: PERSPECTIVES[sourceVersion] })
        .fetch<SourceQueryResult[]>(DOCUMENTS_BY_CRUISE_CODE_QUERY, {
          type,
          cruiseCode: debouncedCruiseCode
        })
  });

  const source = sources.data?.length === 1 ? sources.data[0] : undefined;
  const isSelfClone = source && publishedId(source._id) === targetId;
  const sourceIsDraft = source?._originalId?.startsWith("drafts.");

  const lookupMessage = useMemo(() => {
    if (!debouncedCruiseCode || sources.isLoading) return undefined;
    if (sources.isError) return `Lookup failed: ${sources.error.message}`;
    if (sources.data?.length === 0)
      return `No ${typeTitle} found with code "${debouncedCruiseCode}".`;
    if ((sources.data?.length ?? 0) > 1)
      return `${sources.data?.length} documents of type ${typeTitle} found with code "${debouncedCruiseCode}".`;
    if (isSelfClone) return `This is the current ${typeTitle}.`;
    return undefined;
  }, [debouncedCruiseCode, sources, isSelfClone, typeTitle]);

  const handleClone = () => {
    if (!source) return;
    setCloning(true);

    const { set, unset } = cloneContent(source, fields, excludedFields);
    const draftId = `drafts.${targetId}`;

    client
      .transaction()
      .createIfNotExists({ ...target, _id: draftId })
      .patch(draftId, (p) => p.set(set).unset(unset))
      .commit({ autoGenerateArrayKeys: true })
      .then(() => {
        toast.push({ status: "success", title: "Content cloned to draft." });
        onCloned();
      })
      .catch((err) => {
        toast.push({
          status: "error",
          title: err.details?.description ?? err.message
        });
        setCloning(false);
      });
  };

  return (
    <Box padding={4}>
      <Stack gap={4}>
        <Stack gap={2}>
          <Text size={1} weight="semibold">
            Source cruise code
          </Text>
          <TextInput
            value={cruiseCode}
            onChange={(e) => setCruiseCode(e.currentTarget.value)}
            placeholder="e.g. SH260101007"
            autoFocus
          />
        </Stack>

        <Flex gap={4}>
          {(["published", "draft"] as const).map((version) => (
            <Flex key={version} gap={2} align="center">
              <Radio
                id={`clone-source-${version}`}
                checked={sourceVersion === version}
                onChange={() => setSourceVersion(version)}
              />
              <Text as="label" htmlFor={`clone-source-${version}`} size={1}>
                {version === "published"
                  ? "Published"
                  : "Draft (falls back to published)"}
              </Text>
            </Flex>
          ))}
        </Flex>

        {sources.isFetching && <Spinner muted />}

        {lookupMessage && (
          <Card padding={3} radius={2} tone="caution">
            <Text size={1}>{lookupMessage}</Text>
          </Card>
        )}

        {source && !isSelfClone && (
          <Stack gap={2}>
            <Card padding={2} radius={2} border>
              <Preview value={source} schemaType={schemaType} />
            </Card>
            <Text size={1} muted>
              Cloning from the {sourceIsDraft ? "draft" : "published"} version.
            </Text>
          </Stack>
        )}

        <Card padding={3} radius={2} tone="critical">
          <Text size={1}>
            The content fields of the current {typeTitle} will be overwritten in
            its draft. Kept fields: {keptFieldTitles}.
          </Text>
        </Card>

        <Button
          tone="primary"
          icon={CopyIcon}
          text="Clone content"
          disabled={!source || isSelfClone || cloning}
          loading={cloning}
          onClick={handleClone}
        />
      </Stack>
    </Box>
  );
};

export default CloneContentDialog;
